import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  Chip,
  Stack
} from '@mui/material';
import {
  FitnessCenter,
  LocalFireDepartment,
  Spa,
  DirectionsRun,
  SelfImprovement,
  SportsGymnastics,
  Lightbulb,
  Shuffle
} from '@mui/icons-material';
import bootcamp from '../assets/YOGA PIC II.jpg';
import flow from '../assets/TK2_7814-Enhanced-NR.jpg';
import pilates from '../assets/SCUPLT II-edit.jpg';
import sculpt from '../assets/SCUPLT I.jpg';
import fusion from '../assets/PUMP PIC II OR FUSION.jpg';
import pump from '../assets/PUMP PIC I.jpg';
import barre from '../assets/BARRE PIC II.jpg';
import candlelight from '../assets/IMG_5038.jpeg';
import SnowEffect from '../components/SnowEffect';

interface ClassInfoItem {
  id: number;
  name: string;
  description: string;
  img: string;
  searchString: string;
  icon: React.ReactNode;
  category: string;
  intensity: 'Low' | 'Medium' | 'High';
  duration: string;
}

const classInfo: ClassInfoItem[] = [
  {
    id: 1,
    name: "Burn Sculpt",
    description: "Combining elements of Pilates, yoga, and cardio into one amazing workout, including weighted movements and cardio bursts.",
    img: sculpt,
    searchString: "Burn Sculpt",
    icon: <FitnessCenter />,
    category: "Strength & Cardio",
    intensity: "High",
    duration: "45-60 min"
  },
  {
    id: 2,
    name: "Burn Barre",
    description: "Combining movements from yoga, Pilates, sculpt, and ballet into one great exercise routine that will elongate and tone your muscles. These classes will work on controlled and isolated movements to go deep into the muscles, utilizing a ballet barre and incorporating several different modalities of fitness.",
    img: barre,
    searchString: "Burn Barre",
    icon: <SportsGymnastics />,
    category: "Tone & Lengthen",
    intensity: "Medium",
    duration: "45-60 min"
  },
  {
    id: 3,
    name: "Burn Bootcamp",
    description: "This class will have you firing on all cylinders! We'll mix it up every time, combining movements from barre, sculpt, pump and cardio.",
    img: bootcamp,
    searchString: "Burn Bootcamp",
    icon: <DirectionsRun />,
    category: "High Intensity",
    intensity: "High",
    duration: "45-60 min"
  },
  {
    id: 4,
    name: "Burn Flow",
    description: "Our signature yoga flow in which each instructor brings their own unique style to the class. Every time you step on your mat is a truly authentic experience. This vinyasa flow-inspired class will teach you how to incorporate your breath and movement. Burn Flow is great for beginners as well as the experienced yogi.",
    img: flow,
    searchString: "Burn Flow",
    icon: <SelfImprovement />,
    category: "Mind & Body",
    intensity: "Low",
    duration: "60-75 min"
  },
  {
    id: 5,
    name: "Burn Pilates",
    description: "Pilates is a low-impact exercise method emphasizing core stability, posture alignment, and controlled, precise movements. Performed on a mat, Burn Pilates utilizes your body weight and small props to challenge and enhance your practice.",
    img: pilates,
    searchString: "Burn Pilates",
    icon: <Spa />,
    category: "Core & Stability",
    intensity: "Low",
    duration: "45-60 min"
  },
  {
    id: 6,
    name: "Burn Pump",
    description: "Burn Pump is a high-energy, full-body strength training workout designed to sculpt, tone, and strengthen your muscles utilizing cardio barbell sets. Set to motivating music, this class targets all major muscle groups, providing a comprehensive and effective workout that will leave you feeling empowered and energized.",
    img: pump,
    searchString: "Burn Pump",
    icon: <FitnessCenter />,
    category: "Strength Training",
    intensity: "High",
    duration: "45-60 min"
  },
  {
    id: 7,
    name: "Burn Candlelight Flow",
    description: "Connect your breath, mind, spirit and body in our yoga candlelit flow class. Lights are dim, candles are lit - an opportunity to allow yourself to block out the distractions and give yourself permission to dive deeper into your practice.",
    img: candlelight,
    searchString: "Burn Candlelight Yoga",
    icon: <Lightbulb />,
    category: "Mindful Practice",
    intensity: "Low",
    duration: "60-75 min"
  },
  {
    id: 8,
    name: "Burn Fusion",
    description: "Burn Fusion classes are instructors' choice of a combination of two of our signature class's styles - Pump/Sculpt, Pilates/Barre, Sculpt/Barre. No matter what combo, you'll feel the Burn.",
    img: fusion,
    searchString: "Burn Fusion",
    icon: <Shuffle />,
    category: "Mixed Modality",
    intensity: "Medium",
    duration: "45-60 min"
  },
];

const ClassInfo: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#E3F2FD', position: 'relative', overflow: 'hidden' }}>
      {/* Snow Effect */}
      <SnowEffect />
      
      {/* Hero Section */}
      <Box sx={{ 
        py: 8, 
        background: `linear-gradient(135deg, #E3F2FD 0%, ${theme.palette.primary.main}15 100%)`,
        textAlign: 'center',
        position: 'relative',
        zIndex: 2
      }}>
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Box>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                Our Classes
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
              >
                Discover our diverse range of fitness classes designed to challenge, inspire, and transform you. 
                From high-intensity workouts to mindful practices, we have something for every fitness level and preference.
              </Typography>
              
              {/* Class Categories Overview */}
              <Grid container spacing={3} sx={{ maxWidth: 800, mx: 'auto' }}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <FitnessCenter sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Strength
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <SelfImprovement sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Mind & Body
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <DirectionsRun sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Cardio
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <SportsGymnastics sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Flexibility
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Classes Grid */}
      <Container maxWidth="xl" sx={{ py: 8, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4}>
          {classInfo.map((item, index) => (
            <Grid item xs={12} sm={6} lg={4} key={item.id}>
              <Grow in timeout={800 + (index * 100)}>
                <Card
                  elevation={4}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="250"
                    image={item.img}
                    alt={item.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  
                  <CardContent 
                    sx={{ 
                      p: 3, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      flexGrow: 1,
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box>
                      {/* Header with Icon and Title */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 50,
                            height: 50,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            color: 'white',
                            mr: 2,
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Typography
                          variant="h5"
                          component="h3"
                          sx={{ fontWeight: 600, color: 'secondary.main' }}
                        >
                          {item.name}
                        </Typography>
                      </Box>

                      {/* Duration Chip */}
                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip
                          label={item.duration}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      </Stack>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.6,
                          mb: 3,
                          minHeight: 80,
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Box>

                    {/* CTA Button - Now positioned at bottom */}
                    <Button
                      variant="contained"
                      color="secondary"
                      fullWidth
                      href={`/classes/schedule?classesSelected=${item.searchString}`}
                      sx={{
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        mt: 'auto',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                        },
                      }}
                    >
                      View Schedule
                    </Button>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call to Action Section */}
      <Box sx={{ py: 8, bgcolor: '#E3F2FD', position: 'relative', zIndex: 2 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700, mb: 3 }}
          >
            Ready to Feel The Burn?
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
          >
            Book your first class today and experience the transformative power of our heated fitness classes. 
            All levels welcome - we'll meet you where you are and help you grow from there.
          </Typography>
          
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={3}
            sx={{ justifyContent: 'center' }}
          >
            <Button
              variant="contained"
              size="large"
              href="/classes/schedule"
              startIcon={<FitnessCenter />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.2)',
                },
              }}
            >
              View Class Schedule
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              href="/membership"
              startIcon={<LocalFireDepartment />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                borderColor: 'secondary.main',
                color: 'secondary.main',
                '&:hover': {
                  borderColor: 'secondary.main',
                  bgcolor: 'secondary.main',
                  color: 'white',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Explore Memberships
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default ClassInfo;
